import React from "react";
import ReactDOM from "react-dom/client";
// Import order matters: this has to come before `./App` (see its own doc comment) — everything
// `App` transitively imports, including plain module-scope code in a statically-imported screen,
// has to be able to assume `setHostAdapter` already ran.
import "./host/bootstrapHostAdapter";
import "./reset.css";
import "./index.css";
import App from "./App";
import CustomerApiService from "./CustomerApiService";

/**
 * Token capture + exchange — kicked off BEFORE the React tree mounts, exactly like
 * vault-frontend's `ApiService.captureTokenFromUrl()` call ordering (see
 * apps/vault-frontend/src/index.tsx), but with customer-stack behavior per
 * the customer-portal spec §2.2 as amended by the cookie-auth cutover
 * (Monday 12656019094, full cutover):
 *   - A token found in the URL is stripped via `history.replaceState`
 *     immediately (security: keeps it out of server logs / copy-pasted
 *     URLs) and POSTed to `/session/exchange`, which sets an httpOnly
 *     session cookie. Nothing is written to `localStorage` anymore — see
 *     `CustomerApiService`'s doc comment.
 *   - No token in the URL is the NORMAL case on a page refresh, not just
 *     "never had a session": the session lives in an httpOnly cookie,
 *     invisible to JS, so there's no client-readable way to tell "refresh
 *     with a still-valid cookie" apart from "no session at all" before
 *     making a request. Both mount the app and let its first API call
 *     decide. A missing/invalid cookie 401s, which `useInterfaceAccess`
 *     turns into its "denied: unauthenticated" state (placeholder chrome +
 *     an inline notice offering a "Sign in at iconik" link).
 *
 * The React root mounts immediately and unconditionally, in parallel with the exchange call
 * rather than after it — so the matched route's lazy JS chunk starts downloading right away and
 * a loading shell paints instead of a blank screen for however long the exchange takes.
 * `CustomerApiService.fetch` is the thing that actually waits on the exchange (see
 * `beginBootExchange`/`bootExchange` there) before firing any request, so no authenticated call
 * can race the Set-Cookie response even though nothing here blocks on it.
 *
 * No `IS_DEV` branch here (there used to be one, gating an old hard-redirect): both the real
 * "no session" case and DevHome's own token-paste flow (`features/dev/DevHome.tsx`) mount
 * unconditionally and are handled entirely by `AppRouter`'s own `IS_DEV`-gated route table.
 */
function mount(): void {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

const fromUrl = new URLSearchParams(window.location.search).get(
  "authorization_token"
);

if (fromUrl) {
  window.history.replaceState(null, "", window.location.pathname);
  CustomerApiService.beginBootExchange(fromUrl);
}

mount();
